import { useCallback, useMemo } from 'react';

import { SearchbarSubmittedProperties } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { SearchSuggestionType } from '../../../resources/searchSuggestions/domain/types/searchSuggestions';
import { SearchOverlayOrigin } from '../SearchOverlay.types';

type SearchbarBlock = 'cover' | 'primary_nav';

export type AmplitudeDestinationType = NonNullable<
    SearchbarSubmittedProperties['destination_type']
>;

export type SearchSubmissionTracking = {
    amplitudeDestinationType: AmplitudeDestinationType;
    label: string;
};

type SearchSubmittedPayload = SearchSubmissionTracking & {
    searchValue: string;
    scrollingMenuClicked: boolean;
};

function resolveBlock(origin: SearchOverlayOrigin): SearchbarBlock {
    return origin === 'cover' ? 'cover' : 'primary_nav';
}

export function destinationTypeFromSuggestion(
    type: SearchSuggestionType,
): AmplitudeDestinationType {
    switch (type) {
        case 'destination':
            return 'country';
        case 'region':
            return 'region';
        case 'place':
            return 'poi';
    }
}

/**
 * Groups the four Amplitude Searchbar events (`clicked`, `typed`, `submitted`, `closed`)
 * behind stable callbacks, pre-filled with the `block` property derived from where the
 * overlay was opened (`cover` for the home cover, `primary_nav` elsewhere).
 *
 * Callers fire the events without repeating the block-resolution logic or pulling the
 * Amplitude client each time.
 */
export function useSearchTracking(origin: SearchOverlayOrigin) {
    const { trackEvent } = useTrackEventAmplitude();
    const block = useMemo(() => resolveBlock(origin), [origin]);

    const trackSearchbarClicked = useCallback(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.searchbarClicked({ ...defaultProperties, block });
        });
    }, [block, trackEvent]);

    const trackSearchbarTyped = useCallback(
        (searchValue: string) => {
            trackEvent((ampli, defaultProperties) => {
                ampli.searchbarTyped({
                    ...defaultProperties,
                    block,
                    search_value: searchValue,
                });
            });
        },
        [block, trackEvent],
    );

    const trackSearchbarSubmitted = useCallback(
        ({
            searchValue,
            label,
            amplitudeDestinationType,
            scrollingMenuClicked,
        }: SearchSubmittedPayload) => {
            trackEvent((ampli, defaultProperties) => {
                ampli.searchbarSubmitted({
                    ...defaultProperties,
                    block,
                    scrolling_menu_clicked: scrollingMenuClicked,
                    search_value: searchValue,
                    search_value_selected: label,
                    destination_type: amplitudeDestinationType,
                });
            });
        },
        [block, trackEvent],
    );

    const trackSearchbarClosed = useCallback(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.searchbarClosed({ ...defaultProperties, block });
        });
    }, [block, trackEvent]);

    return {
        trackSearchbarClicked,
        trackSearchbarTyped,
        trackSearchbarSubmitted,
        trackSearchbarClosed,
    };
}
